import { Separator, Container } from "@radix-ui/themes";
import Head from "../../lib/Head";
import Hero from "../../components/Hero";
import Highlights from "../../components/Highlights"; 
import { pathOf } from "../../lib/routes";
import UspStrip from "../../components/UspStrip";
import GalleryTeaser from "../../components/GalleryTeaser";
import Reviews from "../../components/Reviews";

import LocationAndDistances from "../../components/LocationAndDistances";
import PracticalInfo from "../../components/PracticalInfo";
import { getSeoMeta } from "../../i18n/seo";
import { chooseLang } from "../../lib/lang";
import type { Lang } from "../../lib/lang";
import { HOME_USP_ICONS } from "../../lib/icons";
import { buildVacationRentalSchema } from "../../lib/structuredData";
import {
  ACTIVITY_ROOM_HIGHLIGHT_IMAGE_URL,
  ACTIVITY_ROOM_PHOTOS_IMAGE_URL,
} from "../../data/gallery";

export default function Home({ lang }: { lang: Lang }) {
  const t = (da: string, en: string, de = en) =>
    chooseLang(lang, da, en, de);

  const meta = getSeoMeta(lang, "home");

  /** ---------- HERO TEKSTER (visuelt indhold) ---------- */
  const heroTitle = t(
    "Familievenligt sommerhus i Fjellerup – tæt på strand og skov",
    "Family holiday home in Fjellerup near beach and forest",
    "Familienfreundliches Ferienhaus in Fjellerup nahe Strand und Wald"
  );
  const heroSubtitle = t(
    "Pool, vildmarksbad, sauna, aktivitetsrum og god plads til hele familien.",
    "Pool, hot tub, sauna, activity room and plenty of space for the whole family.",
    "Pool, Whirlpool, Sauna, Aktivitätsraum und viel Platz für die ganze Familie."
  );

  // Structured data for SEO (uses centralized meta without affecting the hero)
  const jsonLd = buildVacationRentalSchema({
    url: "https://fyrrehaven-61.dk",
    description: meta.description,
    amenities: [
      { name: t("Opvarmet udendørs pool", "Heated outdoor pool", "Beheizter Außenpool"), description: t("Åben 1. maj – 1. oktober", "Open May 1 to October 1", "Geöffnet 1. Mai bis 1. Oktober") },
      { name: t("Elektrisk vildmarksbad", "Electric hot tub", "Elektrischer Whirlpool") },
      { name: t("Elektrisk sauna", "Electric sauna", "Elektrische Sauna") },
      { name: t("Aktivitetsrum", "Activity room", "Aktivitätsraum"), description: t("Billard, bordtennis, airhockey, dart og projektor", "Billiards, table tennis, air hockey, darts and projector", "Billard, Tischtennis, Airhockey, Darts und Projektor") },
      { name: t("Tæt på strand og skov", "Close to beach and forest", "Nah an Strand und Wald") },
    ],
  });

  return (
    <>
      {/* SEO/meta – kun title/description for søgemaskiner og social previews */}
      <Head
        lang={lang}
        path={pathOf(lang, "home")}
        title={meta.title}
        description={meta.description}
        ogImage={meta.image}
        ogImageAlt={meta.imageAlt}
        jsonLd={jsonLd}
        robots={meta.robots}
        keywords={meta.keywords}
      />
      <UspStrip
        ariaLabel={t("Hurtige fakta", "Quick facts", "Schnelle Fakten")}
        items={[
          {
            icon: <HOME_USP_ICONS.Response />,
            text: t(
              "Lynhurtigt svar på henvendelser",
              "Lightning-fast replies",
              "Blitzschnelle Antworten"
            ),
            ariaLabel: t(
              "Vi svarer hurtigt på alle henvendelser",
              "We reply quickly to all inquiries",
              "Wir antworten schnell auf alle Anfragen"
            ),
          },
          {
            icon: <HOME_USP_ICONS.CheckIn />,
            text: t(
              "Nem indtjekning (nøgleboks)",
              "Easy self check-in (key box)",
              "Einfacher Check-in (Schlüsseltresor)"
            ),
            ariaLabel: t(
              "Gæster modtager kode til nøgleboks og kan tjekke ind uden vært til stede",
              "Guests receive a key box code and can check in without the host present",
              "Gäste erhalten einen Schlüsselcode und können ohne Gastgeber einchecken"
            ),
          },
          {
            icon: <HOME_USP_ICONS.Rating />,
            text: t(
              "4,8+ stjerner på Airbnb",
              "4.8+ stars on Airbnb",
              "4,8+ Sterne auf Airbnb"
            ),
            ariaLabel: t(
              "Gæsterne giver os over 4,8 stjerner på Airbnb",
              "Guests rate us 4.8+ stars on Airbnb",
              "Gäste bewerten uns mit über 4,8 Sternen auf Airbnb"
            ),
          },
          {
            icon: <HOME_USP_ICONS.Experience />,
            text: t(
              "+3 års værts erfaring",
              "+3 years hosting experience",
              "+3 Jahre Gastgebererfahrung"
            ),
            ariaLabel: t(
              "Mere end tre års erfaring som værter",
              "More than three years of hosting experience",
              "Mehr als drei Jahre Erfahrung als Gastgeber"
            ),
          },
        ]}
      />
      {/* HERO – separat visuel titel/undertekst + badges/CTA’er */}
      <Hero
        title={heroTitle}
        subtitle={heroSubtitle}
        badges={[
          t("10 gæster", "10 guests", "10 Gäste"),
          t("4 soveværelser", "4 bedrooms", "4 Schlafzimmer"),
          t("2 badværelser", "2 bathrooms", "2 Badezimmer"),
          t(
            "Pool-Vildmarksbad-Sauna",
            "Pool-Hot tub-Sauna",
            "Pool-Whirlpool-Sauna"
          ),
          t("Aktivitetsrum", "Activity room", "Aktivitätsraum"),
        ]}
        primaryCta={{
          label: t("Book nu", "Book now", "Jetzt buchen"),
          href: pathOf(lang, "book"),
        }}
        secondaryCta={{
          label: t("Se huset", "See the house", "Haus ansehen"),
          to: pathOf(lang, "house"),
        }}
        media={{
          type: "image",
          src: "https://media.fyrrehaven-61.dk/wp-content/uploads/2025/09/IMG_3736.webp",
          title: t(
            "Fyrrehaven 61 – feriehus ved Fjellerup Strand",
            "Fyrrehaven 61 – holiday home by Fjellerup Beach",
            "Fyrrehaven 61 – Ferienhaus bei Fjellerup Strand"
          ),
          alt: t(
            "Skov og strand ved sommerhuset",
            "Forest and beach near the house",
            "Wald und Strand beim Ferienhaus"
          ),
        }}
        align="left"
      />

      <Separator size="4" />

      {/* Højdepunkter / Highlights – opdateret til dine 4 punkter */}
      <Container size="3">
        <Highlights
          title={t("Højdepunkter", "Highlights", "Highlights")}
          items={[
            {
              title: t("Opvarmet udendørs pool", "Heated outdoor pool", "Beheizter Außenpool"),
              body: t(
                "Åben 1. maj – 1. oktober. Perfekt efter strandturen.",
                "Open May 1 – Oct 1. Perfect after a beach day.",
                "Geöffnet 1. Mai – 1. Okt. Perfekt nach einem Strandtag."
              ),
              media: {
                kind: "image",
                src: "https://media.fyrrehaven-61.dk/wp-content/uploads/2025/09/IMG_3720.webp",
                alt: t(
                  "Opvarmet udendørs pool – åben 1. maj til 1. oktober",
                  "Heated outdoor pool – open May 1 to Oct 1",
                  "Beheizter Außenpool – geöffnet 1. Mai bis 1. Okt."
                ),
                aspect: "4 / 3",
              },
            },
            {
              title: t("Elektrisk vildmarksbad", "Electric hot tub", "Elektrischer Whirlpool"),
              body: t(
                "Autentisk spa oplevelse med elektrisk opvarmning og afslapning under stjernehimlen.",
                "Authentic spa experience with electric heating and relaxation under the stars.",
                "Authentisches Spa-Erlebnis mit elektrischer Wärme und Entspannung unter dem Sternenhimmel."
              ),
              media: {
                kind: "image",
                src: "/highlights/hot-tub-wood.webp",
                alt: t(
                  "Elektrisk vildmarksbad ved Fjellerup Strand",
                  "Electric hot tub at Fjellerup Strand",
                  "Elektrischer Whirlpool bei Fjellerup Strand"
                ),
                aspect: "4 / 3",
              },
            },
            {
              title: t("Sauna", "Sauna"),
              body: t(
                "Hurtig opvarmning og nem betjening, perfekt efter pool eller strand.",
                "Quick heating and easy to use, perfect after pool or beach."
              ),
              media: {
                kind: "image",
                src: "https://media.fyrrehaven-61.dk/wp-content/uploads/2025/09/IMG_3731.webp",
                alt: t(
                  "Sauna i sommerhuset",
                  "Sauna in the holiday home"
                ),
                aspect: "4 / 3",
              },
            },
            {
              title: t("Aktivitetsrum", "Activity room", "Aktivitätsraum"),
              body: t(
                "Spil billard, bordtennis, airhockey og dart – eller sæt film på projektoren.",
                "Play billiards, table tennis, air hockey and darts – or watch movies on the projector.",
                "Spielen Sie Billard, Tischtennis, Airhockey und Darts – oder schauen Sie Filme über den Projektor."
              ),
              media: {
                kind: "image",
                src: ACTIVITY_ROOM_HIGHLIGHT_IMAGE_URL,
                objectPosition: "55% 58%",
                alt: t(
                  "Aktivitetsrum med spil og projektor",
                  "Activity room with games and projector",
                  "Aktivitätsraum mit Spielen und Projektor"
                ),
                aspect: "4 / 3",
              },
            },
            {
              title: t("Fantastisk område", "Fantastic surroundings"),
              body: t(
                "Skov og strand tæt på stier, natur og ro i kort afstand.",
                "Forest and beach close to trails, nature and tranquility nearby."
              ),
              media: {
                kind: "image",
                src: "https://media.fyrrehaven-61.dk/wp-content/uploads/2025/09/IMG_3663.webp",
                alt: t(
                  "Skov og strand tæt på sommerhuset",
                  "Forest and beach close to the holiday home"
                ),
                aspect: "4 / 3",
              },
            },
          ]}
        />
      </Container>
      <Separator size="4" />
      <GalleryTeaser
        title={t("Billeder", "Photos", "Bilder")}
        subtitle={t(
          "Få et hurtigt indtryk - se stue, aktivitetsrum, pool, vildmarksbad og omgivelser.",
          "Get a quick feel—see the living room, activity room, pool, hot tub and surroundings.",
          "Verschaffen Sie sich einen schnellen Eindruck - sehen Sie Wohnzimmer, Aktivitätsraum, Pool, Whirlpool und Umgebung."
        )}
        items={[
          {
            src: ACTIVITY_ROOM_PHOTOS_IMAGE_URL,
            objectPosition: "48% 60%",
            alt: t(
              "Aktivitetsrum med spil og projektor",
              "Activity room with games and projector",
              "Aktivitätsraum mit Spielen und Projektor"
            ),
          },
          {
            src: "https://media.fyrrehaven-61.dk/wp-content/uploads/2025/09/IMG_3668.webp",
            alt: t("Køkken-alrum", "Kitchen-living area", "Küche-Wohnbereich"),
          },
          {
            src: "https://media.fyrrehaven-61.dk/wp-content/uploads/2025/09/IMG_3692.webp",
            alt: t("Opvarmet udendørs pool", "Heated outdoor pool", "Beheizter Außenpool"),
          },
          {
            src: "https://media.fyrrehaven-61.dk/wp-content/uploads/2025/09/IMG_3696.webp",
            alt: t("Elektrisk vildmarksbad", "Electric hot tub", "Elektrischer Whirlpool"),
          },
          {
            src: "https://media.fyrrehaven-61.dk/wp-content/uploads/2025/09/IMG_3709.webp",
            alt: t("El-sauna", "Electric sauna", "Elektrische Sauna"),
          },
          {
            src: "https://media.fyrrehaven-61.dk/wp-content/uploads/2025/09/2c0c5a11-59f7-4ddf-9fda-10068650f7a1.webp",
            alt: t("Skovsti mod stranden", "Forest path to the beach", "Waldweg zum Strand"),
          },
          {
            src: "https://media.fyrrehaven-61.dk/wp-content/uploads/2025/09/IMG_3669.webp",
            alt: t("Stue med lysindfald", "Living room with daylight", "Wohnzimmer mit Tageslicht"),
          },
          // du kan have flere – +N overlay vises automatisk hvis items.length > max
        ]}
        cta={{
          label: t("Åbn galleri", "Open gallery", "Galerie öffnen"),
          to: pathOf(lang, "gallery"),
        }}
        align="center"
      />
      {/* Din oprindelige FEATURES-sektion forbliver uændret */}
      <Separator size="4" />
      <Reviews
        lang={lang}
        average={4.8}
        showSchema={false}
      />
      <PracticalInfo
        lang={lang}
        variant="teaser"
        maxItems={6}
        ctaHref={pathOf(lang, "house")}
        ctaLabelDa="Læs mere om huset"
        ctaLabelEn="Read more about the house"
      />
      <LocationAndDistances
        lang={lang}
        mapEmbedUrl="https://www.google.com/maps/d/embed?mid=144jHAnieVKibH7nlt3mRpmImcWVoKic&ehbc=2E312F"
        mapLinkUrl="https://www.google.com/maps/d/viewer?mid=144jHAnieVKibH7nlt3mRpmImcWVoKic"
        directionsTo="Fyrrehaven 61, 8500"
        title={
          lang === "da"
            ? "Beliggenhed & afstande"
            : lang === "de"
            ? "Lage & Entfernungen"
            : "Location & distances"
        }
        subtitle={
          lang === "da"
            ? "Skovområde med stier – kort tur til stranden."
            : lang === "de"
            ? "Waldgebiet mit Wegen – kurzer Fußweg zum Strand."
            : "Forest area with trails — a short walk to the beach."
        }
      />
    </>
  );
}
